// Dynamic Store Locator - Mapbox GL JS Implementation
// Reliable alternative to Leaflet with better rendering

(function() {
  'use strict';

  class MapboxStockists {
    constructor() {
      this.stores = [];
      this.map = null;
      this.markers = [];
      this.currentFilter = '';
      this.defaultCountry = 'United Kingdom';
      this.mapboxToken = '';
      
      console.log('🗺️ Initializing Mapbox Stockists...');
      this.loadStoreData();
    }

    loadStoreData() {
      try {
        const dataElement = document.getElementById('stores-data');
        if (!dataElement) {
          console.error('❌ Store data element not found');
          this.showError('Store data not found');
          return;
        }

        const data = JSON.parse(dataElement.textContent);
        this.stores = data.stores || [];
        this.defaultCountry = data.defaultCountry || 'United Kingdom';
        this.mapboxToken = data.mapboxToken || '';
        
        console.log('✅ Loaded', this.stores.length, 'stores');
        
        if (!this.mapboxToken || this.mapboxToken === 'YOUR_MAPBOX_TOKEN_HERE') {
          this.showError('Please add your Mapbox token to the template');
          return;
        }

        if (this.stores.length === 0) {
          this.showError('No stores configured yet');
          return;
        }

        this.init();
      } catch (error) {
        console.error('❌ Error loading store data:', error);
        this.showError('Error loading store data');
      }
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    setup() {
      console.log('🚀 Setting up store locator...');
      
      if (typeof mapboxgl === 'undefined') {
        console.error('❌ Mapbox GL not loaded');
        this.showError('Map library not loaded');
        return;
      }

      this.initMap();
      this.populateCountryFilter();
      this.bindEvents();
      
      // Auto-select default country (United Kingdom) on page load
      setTimeout(() => {
        const filterSelect = document.getElementById('country-filter');
        if (filterSelect && this.defaultCountry) {
          filterSelect.value = this.defaultCountry;
          console.log('🔍 Auto-selecting default country:', this.defaultCountry);
          this.filterByCountry(this.defaultCountry);
        } else {
          // Fallback: show nothing if no default country
          console.log('🔍 No default country - starting empty');
          this.filterByCountry('');
        }
      }, 1000);
    }

    initMap() {
      console.log('📍 Initializing Mapbox map...');
      
      mapboxgl.accessToken = this.mapboxToken;
      
      this.map = new mapboxgl.Map({
        container: 'stockists-map',
        style: 'mapbox://styles/mapbox/light-v11', // Clean light theme
        center: [-2.0, 54.5], // UK center
        zoom: 5.5,
        minZoom: 2,
        maxZoom: 18
      });

      // Add navigation controls
      this.map.addControl(new mapboxgl.NavigationControl(), 'top-left');

      this.map.on('load', () => {
        console.log('✅ Map loaded successfully');
        this.hideLoading();
        
        // Make sure markers are visible after map loads
        console.log('🎯 Map is ready for markers');
      });

      this.map.on('error', (e) => {
        console.error('❌ Map error:', e);
      });

      // Log when style is loaded (important for marker rendering)
      this.map.on('style.load', () => {
        console.log('✅ Map style loaded - markers should be visible');
      });

      console.log('✅ Map initialized');
    }

    addMarkers(stores) {
      console.log('📍 Starting to add markers for', stores.length, 'stores');
      console.log('Full store data:', JSON.stringify(stores, null, 2));
      
      // Remove existing markers
      this.markers.forEach(marker => marker.remove());
      this.markers = [];

      let successCount = 0;
      let failCount = 0;

      stores.forEach((store, index) => {
        console.log(`\n--- Processing store ${index + 1}: ${store.name} ---`);
        console.log('Raw latitude:', store.latitude, 'Type:', typeof store.latitude);
        console.log('Raw longitude:', store.longitude, 'Type:', typeof store.longitude);
        
        const lat = parseFloat(store.latitude);
        const lng = parseFloat(store.longitude);
        
        console.log('Parsed latitude:', lat, 'Valid?', !isNaN(lat));
        console.log('Parsed longitude:', lng, 'Valid?', !isNaN(lng));
        
        if (isNaN(lat) || isNaN(lng)) {
          console.error('❌ INVALID coordinates for:', store.name);
          console.error('   Latitude:', store.latitude, '-> parsed to:', lat);
          console.error('   Longitude:', store.longitude, '-> parsed to:', lng);
          failCount++;
          return;
        }

        console.log('✅ Valid coordinates - creating marker at', lat, lng);

        // Create popup
        const popup = new mapboxgl.Popup({ 
          offset: 25,
          closeButton: true,
          closeOnClick: true,
          className: 'store-popup-container',
          maxWidth: '300px'
        }).setHTML(this.createPopupContent(store));

        // Use default Mapbox marker with black color
        const marker = new mapboxgl.Marker({
          color: '#000000',
          scale: 1.2
        })
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(this.map);

        // Add click handler for card highlighting
        marker.getElement().addEventListener('click', () => {
          this.highlightStoreCard(store.id);
        });

        this.markers.push(marker);
        successCount++;
        console.log('✅ Marker added successfully');
      });

      console.log('\n=== MARKER SUMMARY ===');
      console.log('✅ Successfully added:', successCount, 'markers');
      console.log('❌ Failed to add:', failCount, 'markers');
      console.log('📍 Total markers on map:', this.markers.length);
      
      // Log all marker positions
      this.markers.forEach((marker, index) => {
        const lngLat = marker.getLngLat();
        console.log(`📍 Marker ${index + 1} position: lng=${lngLat.lng.toFixed(4)}, lat=${lngLat.lat.toFixed(4)}`);
      });
    }

    createPopupContent(store) {
      let html = '<div class="store-popup">';
      html += `<h3>${this.escapeHtml(store.name)}</h3>`;
      
      const addressParts = [];
      if (store.address) addressParts.push(store.address);
      if (store.city) addressParts.push(store.city);
      if (addressParts.length > 0) {
        html += `<p>${this.escapeHtml(addressParts.join(', '))}</p>`;
      }
      
      if (store.phone) {
        html += `<p><a href="tel:${this.escapeHtml(store.phone)}">${this.escapeHtml(store.phone)}</a></p>`;
      }
      if (store.email) {
        html += `<p><a href="mailto:${this.escapeHtml(store.email)}">${this.escapeHtml(store.email)}</a></p>`;
      }
      if (store.website) {
        html += `<p><a href="${this.escapeHtml(store.website)}" target="_blank" rel="noopener">Visit Website</a></p>`;
      }
      
      html += `<p class="country">${this.escapeHtml(store.country)}</p>`;
      html += '</div>';
      
      return html;
    }

    populateCountryFilter() {
      const countries = [...new Set(this.stores.map(store => store.country))].sort();
      const filterSelect = document.getElementById('country-filter');
      
      if (!filterSelect) return;

      filterSelect.innerHTML = '<option value="">PLEASE SELECT COUNTRY</option>';

      countries.forEach(country => {
        const option = document.createElement('option');
        option.value = country;
        option.textContent = country.toUpperCase();
        if (country === this.defaultCountry) option.selected = true;
        filterSelect.appendChild(option);
      });

      console.log('✅ Country filter populated');
    }

    displayStores(stores) {
      const grid = document.getElementById('stores-grid');
      if (!grid) return;

      grid.innerHTML = '';

      if (stores.length === 0) {
        // Show different message based on whether a country is selected
        const message = this.currentFilter 
          ? 'No stores found for this country.' 
          : 'Please select a country to view stores.';
        grid.innerHTML = `<div class="empty-state">${message}</div>`;
        return;
      }

      stores.forEach(store => {
        const card = this.createStoreCard(store);
        grid.appendChild(card);
      });
    }

    createStoreCard(store) {
      const card = document.createElement('div');
      card.className = 'store-card';
      card.dataset.storeId = store.id;

      const addressParts = [];
      if (store.address) addressParts.push(store.address);
      if (store.city) addressParts.push(store.city);
      const addressText = addressParts.join('<br>');

      let html = `<h3 class="store-name">${this.escapeHtml(store.name)}</h3>`;
      if (addressText) {
        html += `<p class="store-address">${addressText}</p>`;
      }
      html += `<p class="store-country">${this.escapeHtml(store.country)}</p>`;
      
      if (store.phone || store.website) {
        html += '<div class="store-contact">';
        if (store.phone) {
          html += `<div><a href="tel:${this.escapeHtml(store.phone)}">${this.escapeHtml(store.phone)}</a></div>`;
        }
        if (store.website) {
          html += `<div><a href="${this.escapeHtml(store.website)}" target="_blank">Visit Website</a></div>`;
        }
        html += '</div>';
      }

      card.innerHTML = html;

      card.addEventListener('click', () => {
        this.focusOnStore(store);
      });

      return card;
    }

    focusOnStore(store) {
      const lat = parseFloat(store.latitude);
      const lng = parseFloat(store.longitude);
      
      if (isNaN(lat) || isNaN(lng)) return;

      this.map.flyTo({
        center: [lng, lat],
        zoom: 15,
        duration: 1500
      });

      const marker = this.markers.find(m => {
        const lngLat = m.getLngLat();
        return Math.abs(lngLat.lat - lat) < 0.0001 && Math.abs(lngLat.lng - lng) < 0.0001;
      });

      if (marker) {
        marker.togglePopup();
      }

      this.highlightStoreCard(store.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    highlightStoreCard(storeId) {
      document.querySelectorAll('.store-card.active').forEach(card => {
        card.classList.remove('active');
      });
      
      const card = document.querySelector(`.store-card[data-store-id="${storeId}"]`);
      if (card) {
        card.classList.add('active');
      }
    }

    bindEvents() {
      const filterSelect = document.getElementById('country-filter');
      if (filterSelect) {
        filterSelect.addEventListener('change', (e) => {
          this.filterByCountry(e.target.value);
        });
      }

      window.addEventListener('resize', () => {
        if (this.map) {
          this.map.resize();
        }
      });
    }

    filterByCountry(country) {
      console.log('🔍 Filtering by:', country || 'None selected');
      this.currentFilter = country;

      // If no country selected, show empty state
      if (!country || country === '') {
        console.log('⚠️ No country selected - showing empty state');
        this.displayStores([]);
        
        // Remove all markers
        this.markers.forEach(marker => marker.remove());
        this.markers = [];
        
        // Reset map to default view
        this.map.flyTo({ 
          center: [-2.0, 54.5], 
          zoom: 5.5,
          duration: 1000
        });
        return;
      }

      let filteredStores = this.stores.filter(store => store.country === country);

      console.log('📋 Found', filteredStores.length, 'stores to display');

      this.displayStores(filteredStores);
      
      // Wait a moment before adding markers to ensure map is ready
      setTimeout(() => {
        console.log('🗺️ Map ready state:', this.map.loaded() ? 'LOADED' : 'LOADING');
        this.addMarkers(filteredStores);
        this.fitMapToMarkers(filteredStores);
      }, 500);
    }

    fitMapToMarkers(stores) {
      if (!this.map || stores.length === 0) {
        this.map.flyTo({ center: [-2.0, 54.5], zoom: 5.5 });
        return;
      }

      if (stores.length === 1) {
        const lat = parseFloat(stores[0].latitude);
        const lng = parseFloat(stores[0].longitude);
        this.map.flyTo({ center: [lng, lat], zoom: 13 });
        return;
      }

      const bounds = new mapboxgl.LngLatBounds();
      stores.forEach(store => {
        const lat = parseFloat(store.latitude);
        const lng = parseFloat(store.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          bounds.extend([lng, lat]);
        }
      });

      this.map.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15,
        duration: 1000
      });
    }

    hideLoading() {
      const spinner = document.getElementById('map-loading');
      if (spinner) spinner.style.display = 'none';
    }

    showError(message) {
      const spinner = document.getElementById('map-loading');
      if (spinner) {
        spinner.innerHTML = `<p style="color: #dc2626;">${this.escapeHtml(message)}</p>`;
      }
      
      const grid = document.getElementById('stores-grid');
      if (grid) {
        grid.innerHTML = `<div class="empty-state" style="color: #dc2626;">${this.escapeHtml(message)}</div>`;
      }
    }

    escapeHtml(text) {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return String(text).replace(/[&<>"']/g, m => map[m]);
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new MapboxStockists();
    });
  } else {
    new MapboxStockists();
  }

})();
